// frontend/apps/widgets/src/widgets/GiftVoucher/components/GiftVoucherGrid.js
import React, {useCallback} from 'react';
import PropTypes from 'prop-types';
import {Grid, Box} from '@mui/material';
import {GiftVoucherCard, CustomVoucherCard} from 'packages/common/components/gift-voucher';
import {PRIMARY} from 'packages/common/theme/palette';

const FALLBACK_PUBLIC_URL = 'https://go.brydge.app';

function GiftVoucherGrid({
  voucherTypes,
  categories,
  companyDetails,
  primaryColor,
  customVoucherConfig,
  displayMode = 'fixed',
  currentLanguage,
}) {
  const usedPrimaryColor = primaryColor || companyDetails?.branding?.primaryColor || companyDetails?.primaryColor || PRIMARY.main;
  const isFloating = displayMode === 'floating';

  const handleVoucherNavigate = useCallback((voucher) => {
    const appBaseUrl = process.env.REACT_APP_PUBLIC_URL || FALLBACK_PUBLIC_URL;
    const voucherId = voucher.id || voucher._id;
    const langParam = currentLanguage ? `?lang=${currentLanguage}` : '';
    window.open(`${appBaseUrl}/${companyDetails.slug}/buy/${voucherId}${langParam}`, '_blank');
  }, [companyDetails?.slug, currentLanguage]);

  const handleCustomVoucherNavigate = useCallback(() => {
    const appBaseUrl = process.env.REACT_APP_PUBLIC_URL || FALLBACK_PUBLIC_URL;
    const langParam = currentLanguage ? `?lang=${currentLanguage}` : '';
    window.open(`${appBaseUrl}/${companyDetails.slug}/create-custom-voucher${langParam}`, '_blank');
  }, [companyDetails?.slug, currentLanguage]);

  if (!voucherTypes || voucherTypes.length === 0) {
    return null;
  }

  return (
    <Grid
      container
      spacing={isFloating ? {xs: 3, sm: 3, md: 4, lg: 4} : {xs: 4, sm: 4, md: 6, lg: 6}}
      alignItems="center"
      justifyContent="center"
      sx={{
        pt: isFloating ? {xs: 0, sm: 3} : {xs: 2, sm: 6},
        pb: isFloating ? 3 : 4,
        flexDirection: {xs: 'column', sm: 'row'},
      }}
    >
      {voucherTypes.map((item, index) => {
        const uniqueKey = item.type === 'custom' ?
          'custom-voucher' :
          item.data?._id || item.data?.id || `voucher-${index}`;

        return (
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            lg={4}
            key={uniqueKey}
            width="100%"
          >
            <Box sx={{'position': 'relative', 'zIndex': 1, '&:hover': {zIndex: 2}}}>
              {item.type === 'custom' ? (
                <CustomVoucherCard
                  companyDetails={companyDetails}
                  onNavigate={handleCustomVoucherNavigate}
                />
              ) : (
                <GiftVoucherCard
                  voucher={item.data}
                  categories={categories}
                  companyDetails={companyDetails}
                  color={usedPrimaryColor}
                  displayMode={displayMode}
                  onNavigate={handleVoucherNavigate}
                />
              )}
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
}

GiftVoucherGrid.propTypes = {
  voucherTypes: PropTypes.array.isRequired,
  categories: PropTypes.array,
  companyDetails: PropTypes.object,
  primaryColor: PropTypes.string,
  customVoucherConfig: PropTypes.object,
  displayMode: PropTypes.oneOf(['fixed', 'floating']),
  currentLanguage: PropTypes.string,
};

export default GiftVoucherGrid;
